"use client";

/*
 * NetworkSchematic — a transit-map style drawing of the backbone.
 *
 * Two views:
 *   - "international": a metro-map schematic (45° geometry, not to scale)
 *     of the four corridors. Multi-route segments are drawn as parallel
 *     lines (three for Sofia–Frankfurt, two for Yerevan–Agarak), subsea is
 *     dashed, the partner leg to Dubai is dotted.
 *   - "bulgaria": a radial drawing of the nine Edge PoPs homed on Sofia,
 *     positioned roughly geographically.
 *
 * Pure SVG — no WebGL, no map tiles, no network requests. Renders on the
 * server, works on phones and with JS disabled (selection needs JS).
 */

import { useId } from "react";
import {
  CORRIDOR_INFO,
  SEGMENTS,
  nodeLabel,
  type Segment,
} from "@/data/network-paths";

type Lang = "en" | "bg";
export type SchematicView = "international" | "bulgaria";

type Pt = readonly [number, number];

/* ─── International schematic geometry ─── */
const INTL_NODES: Record<string, Pt> = {
  london: [80, 120],
  frankfurt: [260, 120],
  sofia: [440, 290],
  athens: [440, 470],
  istanbul: [610, 420],
  balchik: [600, 210],
  poti: [780, 210],
  tbilisi: [920, 210],
  yerevan: [920, 350],
  agarak: [920, 470],
  dubai: [1000, 540],
};

/** Polyline points per segment (keyed "a|b"), 45° bends. */
const INTL_PATHS: Record<string, Pt[]> = {
  "sofia|frankfurt": [[440, 290], [440, 270], [290, 120], [260, 120]],
  "frankfurt|london": [[260, 120], [80, 120]],
  "sofia|istanbul": [[440, 290], [570, 420], [610, 420]],
  "sofia|athens": [[440, 290], [440, 470]],
  "sofia|balchik": [[440, 290], [520, 210], [600, 210]],
  "balchik|poti": [[600, 210], [780, 210]],
  "poti|tbilisi": [[780, 210], [920, 210]],
  "poti|yerevan": [[780, 210], [920, 350]],
  "tbilisi|yerevan": [[920, 210], [920, 350]],
  "yerevan|agarak": [[920, 350], [920, 470]],
  "tbilisi|dubai": [[920, 210], [1000, 290], [1000, 540]],
};

/** Label placement relative to the station dot. */
const INTL_LABEL: Record<string, { dx: number; dy: number; anchor: "start" | "middle" | "end" }> = {
  london: { dx: 0, dy: -22, anchor: "middle" },
  frankfurt: { dx: 0, dy: -22, anchor: "middle" },
  sofia: { dx: -22, dy: 6, anchor: "end" },
  athens: { dx: 0, dy: 34, anchor: "middle" },
  istanbul: { dx: 22, dy: 6, anchor: "start" },
  balchik: { dx: 0, dy: -22, anchor: "middle" },
  poti: { dx: 0, dy: -20, anchor: "middle" },
  tbilisi: { dx: 0, dy: -22, anchor: "middle" },
  yerevan: { dx: -20, dy: 6, anchor: "end" },
  agarak: { dx: -20, dy: 6, anchor: "end" },
  dubai: { dx: -20, dy: 6, anchor: "end" },
};

/* ─── Bulgaria radial geometry (equirectangular over 22.3–28.7°E, 41.2–44.3°N) ─── */
const BG_GEO: Record<string, readonly [number, number]> = {
  sofia: [42.6977, 23.3219],
  plovdiv: [42.1354, 24.7453],
  "stara-zagora": [42.4258, 25.6345],
  burgas: [42.5048, 27.4626],
  varna: [43.2141, 27.9147],
  dobrich: [43.5727, 27.8275],
  balchik: [43.4212, 28.1669],
  montana: [43.4096, 23.2257],
  pleven: [43.417, 24.6067],
  blagoevgrad: [42.0119, 23.0991],
};
function bgProject([lat, lng]: readonly [number, number]): Pt {
  const x = 60 + ((lng - 22.3) / (28.7 - 22.3)) * 900;
  const y = 60 + ((44.3 - lat) / (44.3 - 41.2)) * 460;
  return [Math.round(x), Math.round(y)];
}
const BG_NODES: Record<string, Pt> = Object.fromEntries(
  Object.entries(BG_GEO).map(([k, v]) => [k, bgProject(v)]),
);
const BG_LABEL_BELOW = new Set(["plovdiv", "blagoevgrad", "burgas", "stara-zagora"]);
/** The Black Sea cluster (Varna, Balchik, Dobrich) is tight — place labels by hand. */
const BG_LABEL_OVERRIDE: Record<string, { dx: number; dy: number; anchor: "start" | "middle" | "end" }> = {
  varna: { dx: 20, dy: 22, anchor: "start" },
  balchik: { dx: 20, dy: 6, anchor: "start" },
  dobrich: { dx: 0, dy: -20, anchor: "middle" },
};

function key(s: Segment) {
  return `${s.a}|${s.b}`;
}

function pointsAttr(pts: Pt[]) {
  return pts.map(([x, y]) => `${x},${y}`).join(" ");
}

export interface NetworkSchematicProps {
  view: SchematicView;
  lang: Lang;
  /** Segments to emphasise; everything else is dimmed when non-empty. */
  highlight?: readonly Segment[];
  /** Endpoints currently selected (ringed). */
  endpoints?: readonly string[];
  onNodeClick?: (id: string) => void;
  className?: string;
  title: string;
}

export function NetworkSchematic({
  view,
  lang,
  highlight = [],
  endpoints = [],
  onNodeClick,
  className,
  title,
}: NetworkSchematicProps) {
  const titleId = useId();
  const hl = new Set(highlight.map(key));
  const dimming = hl.size > 0;

  const segs = SEGMENTS.filter((s) =>
    view === "international"
      ? key(s) in INTL_PATHS
      : s.corridor === "bulgaria",
  );
  const nodes = view === "international" ? INTL_NODES : BG_NODES;

  const segPoints = (s: Segment): Pt[] =>
    view === "international"
      ? INTL_PATHS[key(s)]
      : [BG_NODES[s.a], BG_NODES[s.b]];

  return (
    <svg
      viewBox={view === "international" ? "0 80 1060 500" : "0 20 1020 560"}
      role="img"
      aria-labelledby={titleId}
      className={className}
      style={{ width: "100%", height: "auto", display: "block" }}
    >
      <title id={titleId}>{title}</title>

      {view === "international" && (
        <g aria-hidden="true" fontSize="13" fill="var(--signal-ink-faint)" letterSpacing="2">
          <text x="690" y="250" textAnchor="middle">
            {lang === "bg" ? "ЧЕРНО МОРЕ" : "BLACK SEA"}
          </text>
          <text x="1000" y="566" textAnchor="end" fontSize="11" letterSpacing="1">
            {lang === "bg" ? "партньор" : "partner"}
          </text>
        </g>
      )}

      {/* Lines */}
      <g fill="none" strokeLinecap="round" strokeLinejoin="round">
        {segs.map((s) => {
          const pts = pointsAttr(segPoints(s));
          const color = CORRIDOR_INFO[s.corridor].color;
          const on = !dimming || hl.has(key(s));
          const opacity = on ? 1 : 0.18;
          const routes = s.routes ?? 1;
          const dash =
            s.medium === "subsea" ? "14 8" : s.medium === "partner" ? "2 9" : undefined;
          return (
            <g key={key(s)} opacity={opacity} style={{ transition: "opacity 200ms" }}>
              {routes >= 2 && !dash ? (
                <>
                  <polyline points={pts} stroke={color} strokeWidth={routes >= 3 ? 16 : 11} />
                  <polyline points={pts} stroke="var(--signal-void)" strokeWidth={routes >= 3 ? 11 : 4.5} />
                  {routes >= 3 && <polyline points={pts} stroke={color} strokeWidth={4.5} />}
                </>
              ) : (
                <polyline
                  points={pts}
                  stroke={color}
                  strokeWidth={on && dimming ? 7 : 6}
                  strokeDasharray={dash}
                />
              )}
            </g>
          );
        })}
      </g>

      {/* Stations */}
      <g>
        {Object.entries(nodes).map(([id, [x, y]]) => {
          const isTransit = id === "poti";
          const isHub = id === "sofia";
          const selected = endpoints.includes(id);
          const onPath =
            !dimming || highlight.some((s) => s.a === id || s.b === id);
          const label =
            view === "international"
              ? INTL_LABEL[id]
              : BG_LABEL_OVERRIDE[id] ?? {
                  dx: 0,
                  dy: BG_LABEL_BELOW.has(id) ? 34 : -20,
                  anchor: "middle" as const,
                };
          const clickable = !!onNodeClick && !isTransit;
          const r = isHub ? 13 : isTransit ? 6 : 9;
          return (
            <g
              key={id}
              opacity={onPath ? 1 : 0.35}
              style={{ cursor: clickable ? "pointer" : undefined, transition: "opacity 200ms" }}
              onClick={clickable ? () => onNodeClick!(id) : undefined}
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onNodeClick!(id);
                      }
                    }
                  : undefined
              }
              tabIndex={clickable ? 0 : undefined}
              role={clickable ? "button" : undefined}
              aria-label={clickable ? nodeLabel(id, lang) : undefined}
              aria-pressed={clickable ? selected : undefined}
            >
              {selected && (
                <circle cx={x} cy={y} r={r + 8} fill="none" stroke="var(--accent)" strokeWidth={3} />
              )}
              <circle
                cx={x}
                cy={y}
                r={r}
                fill="var(--signal-void)"
                stroke={isTransit ? "var(--signal-ink-dim)" : "var(--signal-ink)"}
                strokeWidth={isHub ? 5 : 3.5}
              />
              {/* generous hit area for touch */}
              {clickable && <circle cx={x} cy={y} r={24} fill="transparent" />}
              <text
                x={x + label.dx}
                y={y + label.dy}
                textAnchor={label.anchor}
                fontSize={isHub ? 20 : isTransit ? 13 : 16}
                fontWeight={isHub || selected ? 700 : 500}
                fill={isTransit ? "var(--signal-ink-dim)" : "var(--signal-ink)"}
                style={{ pointerEvents: "none" }}
              >
                {nodeLabel(id, lang)}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
}

/** Small legend explaining line styles. */
export function SchematicLegend({ lang }: { lang: Lang }) {
  const items: { label: string; draw: React.ReactNode }[] = [
    {
      label: lang === "bg" ? "Наземно трасе" : "Terrestrial route",
      draw: <line x1="2" y1="7" x2="34" y2="7" stroke="var(--signal-ink-dim)" strokeWidth="4" strokeLinecap="round" />,
    },
    {
      label: lang === "bg" ? "Защитени паралелни трасета" : "Protected parallel routes",
      draw: (
        <>
          <line x1="2" y1="7" x2="34" y2="7" stroke="var(--signal-ink-dim)" strokeWidth="11" />
          <line x1="2" y1="7" x2="34" y2="7" stroke="var(--signal-void)" strokeWidth="4.5" />
        </>
      ),
    },
    {
      label: lang === "bg" ? "Подводен кабел" : "Subsea cable",
      draw: <line x1="2" y1="7" x2="34" y2="7" stroke="var(--signal-ink-dim)" strokeWidth="4" strokeDasharray="8 5" />,
    },
    {
      label: lang === "bg" ? "Чрез партньор" : "Via partner",
      draw: <line x1="2" y1="7" x2="34" y2="7" stroke="var(--signal-ink-dim)" strokeWidth="4" strokeDasharray="2 6" strokeLinecap="round" />,
    },
  ];
  return (
    <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-[var(--signal-ink-dim)]">
      {items.map((i) => (
        <li key={i.label} className="flex items-center gap-2">
          <svg width="36" height="14" aria-hidden="true">{i.draw}</svg>
          {i.label}
        </li>
      ))}
    </ul>
  );
}
